<script setup lang="ts">
import { computed, getCurrentInstance, inject } from 'vue'
import { isExternalHref } from '../utils/isExternalHref'
import { normalizeExternalHref } from '../utils/normalizeExternalHref'

type To = string | Record<string, any>

const props = withDefaults(defineProps<{
  to?: To
  href?: string
  external?: boolean
  replace?: boolean
  target?: string
  rel?: string
  allowReferrer?: boolean
}>(), {})

// Allow host app to force a Link component (recommended for Nuxt)
const InjectedLink = inject<any>('ui-link-component', null)

// Runtime detection (Nuxt or plain vue-router) without importing them
const inst = getCurrentInstance()
const NuxtLink = inst?.appContext.components?.NuxtLink
const RouterLink = inst?.appContext.components?.RouterLink

const rawHref = computed(() => props.href ?? (typeof props.to === 'string' ? props.to : undefined))

const isExternal = computed(() => {
  return Boolean(props.external || isExternalHref(rawHref.value))
})

const UseComp = computed<any>(() => {
  // External hrefs must bypass injected client-side routers.
  if (isExternal.value) return 'a'

  if (InjectedLink && props.to) return InjectedLink
  if (NuxtLink && props.to) return NuxtLink
  if (RouterLink && props.to) return RouterLink
  return 'a'
})

const isShopexpertsUrl = (url: string | undefined): boolean => {
  if (!url || !/^(?:https?:)?\/\//i.test(url)) return false

  try {
    const hostname = new URL(url, 'https://shopexperts.com').hostname
    return hostname === 'shopexperts.com' || hostname.endsWith('.shopexperts.com')
  } catch {
    return false
  }
}

const defaultExternalRel = computed(() => {
  return props.allowReferrer || isShopexpertsUrl(rawHref.value) ? 'noopener' : 'noopener noreferrer'
})

const bindAttrs = computed(() => {
  if (UseComp.value === 'a') {
    return {
      href: isExternal.value ? normalizeExternalHref(rawHref.value) : rawHref.value,
      target: isExternal.value ? (props.target || '_blank') : props.target,
      rel: isExternal.value ? (props.rel || defaultExternalRel.value) : props.rel
    }
  }
  // NuxtLink / RouterLink
  return { to: props.to, replace: props.replace }
})
</script>

<template>
  <component :is="UseComp" v-bind="bindAttrs">
    <slot />
  </component>
</template>
